/**
 * SOLID / WIPEOUT / gradient + 3D mesh fallback serializers
 * (wipeoutGradient.ts). Flat 2D solids (SOLID 2D, wipeout plates, gradient
 * fills) export as native SOLID entities with the classic corner swap
 * (groups 12/13 hold visual corners 4/3). Full 3D mesh payloads export as
 * a 3DFACE fan so no tessellated geometry is lost. Gradient color stops
 * (HATCH 450-470) arrive with Faz 7's two-tone fill.
 */

import type { Entity } from '../../../../state/store';
import type { DxfWriteContext } from '../types';
import type { DxfStreamWriter } from '../DxfStreamWriter';
import { entityPrologue } from './common';

/** A flat 2D plate (single polygon payload), not a triangulated mesh. */
function isFlat2D(d: Record<string, any>): boolean {
    return !!(d.flat || d.wipeout || d.gradient);
}

/** Ordered boundary ring of a flat solid (same walk entityToPolyline uses). */
function flatRing(d: Record<string, any>): number[][] {
    const verts = Array.isArray(d.vertices) ? (d.vertices as number[][]) : [];
    const idx = Array.isArray(d.indices) ? (d.indices as number[][]) : [];
    if (verts.length === 0 || idx.length === 0) return verts;
    const edgeUse = new Map<string, { count: number; a: number; b: number }>();
    idx.forEach(([a, b, c]) => {
        ([[a, b], [b, c], [c, a]] as [number, number][]).forEach(([x, y]) => {
            const key = x < y ? `${x}:${y}` : `${y}:${x}`;
            const hit = edgeUse.get(key);
            if (hit) hit.count += 1;
            else edgeUse.set(key, { count: 1, a: Math.min(x, y), b: Math.max(x, y) });
        });
    });
    const adj = new Map<number, number[]>();
    edgeUse.forEach((v) => {
        if (v.count !== 1) return;
        adj.set(v.a, [...(adj.get(v.a) ?? []), v.b]);
        adj.set(v.b, [...(adj.get(v.b) ?? []), v.a]);
    });
    const start = adj.keys().next().value as number | undefined;
    if (start === undefined) return verts;
    const loop: number[][] = [verts[start]];
    let prev = -1, cur = start;
    for (let guard = 0; guard < verts.length + 1; guard++) {
        const nexts = (adj.get(cur) ?? []).filter((n) => n !== prev);
        if (nexts.length === 0) break;
        const nxt = nexts[0];
        if (nxt === start) break;
        loop.push(verts[nxt]);
        prev = cur;
        cur = nxt;
    }
    return loop.length >= 3 ? loop : verts;
}

export function writeSolid(w: DxfStreamWriter, ctx: DxfWriteContext, e: Entity, owner: string): void {
    const d = e.data as Record<string, any>;
    const verts = Array.isArray(d.vertices) ? (d.vertices as number[][]) : [];
    const idx = Array.isArray(d.indices) ? (d.indices as number[][]) : [];
    if (verts.length === 0 || idx.length === 0) return;

    if (isFlat2D(d)) {
        const ring = flatRing(d).slice(0, 4);
        while (ring.length < 4) ring.push(ring[ring.length - 1]);
        entityPrologue(w, ctx, e, 'SOLID', owner);
        w.pair(100, 'AcDbTrace');
        const [v0, v1, v2, v3] = ring;
        w.pair(10, v0[0] ?? 0).pair(20, v0[1] ?? 0).pair(30, v0[2] ?? 0);
        w.pair(11, v1[0] ?? 0).pair(21, v1[1] ?? 0).pair(31, v1[2] ?? 0);
        // SOLID draws 1-2-4-3: groups 12/13 carry the 4th/3rd visual corners.
        w.pair(12, v3[0] ?? 0).pair(22, v3[1] ?? 0).pair(32, v3[2] ?? 0);
        w.pair(13, v2[0] ?? 0).pair(23, v2[1] ?? 0).pair(33, v2[2] ?? 0);
        return;
    }

    // 3D mesh payload → 3DFACE fan (one face per triangle, 4th corner = 3rd).
    idx.forEach(([a, b, c], i) => {
        const va = verts[a], vb = verts[b], vc = verts[c];
        if (!va || !vb || !vc) return;
        const face: Entity = { ...e, id: `${e.id}#f${i}` };
        entityPrologue(w, ctx, face, '3DFACE', owner);
        w.pair(100, 'AcDbFace');
        w.pair(10, va[0] ?? 0).pair(20, va[1] ?? 0).pair(30, va[2] ?? 0);
        w.pair(11, vb[0] ?? 0).pair(21, vb[1] ?? 0).pair(31, vb[2] ?? 0);
        w.pair(12, vc[0] ?? 0).pair(22, vc[1] ?? 0).pair(32, vc[2] ?? 0);
        w.pair(13, vc[0] ?? 0).pair(23, vc[1] ?? 0).pair(33, vc[2] ?? 0);
    });
}
